/** The frame of every page (design 11 §2): tenant switcher, search (⌘K), help, the user; the sections. */
import * as Menu from "@radix-ui/react-dropdown-menu";
import { Link, Outlet } from "@tanstack/react-router";
import { Building2, Check, ChevronDown, CircleHelp, Search, UserRound } from "lucide-react";
import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty } from "../ui/states";
import { CommandPalette } from "./CommandPalette";
import { SECTIONS } from "./sections";
import { useBff, useCurrentTenant, useLeave, useMe, useSwitchTenant } from "./session";
import { useTheme, type Theme } from "./theme";
import { useBrand } from "./brand";

const DOCS_URL = "https://github.com/swaralo/swaralo/tree/main/docs";
const MENU = "min-w-56 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-1 shadow-lg";
const ITEM =
  "flex cursor-default items-center gap-2 rounded px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-[var(--color-surface)]";

export function Shell() {
  const t = useT();
  const me = useMe();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [theme, setTheme] = useTheme();

  useEffect(() => {
    const open = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", open);
    return () => window.removeEventListener("keydown", open);
  }, []);

  const bff = useBff();
  const leave = useLeave();
  const brand = useBrand();
  // help: Swaralo's docs, or a brand's own support (none named: no help link, never Swaralo's)
  const help = brand ? (brand.supportEmail ? `mailto:${brand.supportEmail}` : null) : DOCS_URL;
  const signOut = async () => leave((await bff.logout()) ?? "/");

  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:m-2 focus:rounded focus:bg-[var(--color-bg)] focus:p-2">
        {t("app.skip")}
      </a>
      <header className="flex items-center gap-3 border-b border-[var(--color-border)] px-4 py-2">
        <Link to="/" className="mr-2 text-lg font-semibold">
          {brand?.logo ? <img src={brand.logo} alt={t("app.name")} className="h-7 w-auto" /> : t("app.name")}
        </Link>
        <TenantSwitcher />
        <div className="flex-1" />
        <Button variant="secondary" onClick={() => setPaletteOpen(true)} aria-keyshortcuts="Meta+K Control+K">
          <Search size={16} aria-hidden="true" />
          {t("header.search")}
          <kbd className="rounded border border-[var(--color-border)] px-1 text-xs text-[var(--color-muted)]">⌘K</kbd>
        </Button>
        {help && (
          <Button variant="ghost" asChild>
            <a href={help} target="_blank" rel="noreferrer">
              <CircleHelp size={16} aria-hidden="true" />
              {t("header.help")}
            </a>
          </Button>
        )}
        <Menu.Root>
          <Menu.Trigger asChild>
            <Button variant="ghost" aria-label={t("user.menu", { email: me.user.email })}>
              <UserRound size={16} aria-hidden="true" />
              <span className="hidden sm:inline">{me.user.email}</span>
              <ChevronDown size={14} aria-hidden="true" />
            </Button>
          </Menu.Trigger>
          <Menu.Portal>
            <Menu.Content align="end" sideOffset={4} className={MENU}>
              <Menu.Label className="px-2 py-1.5 text-xs text-[var(--color-muted)]">
                {me.role ? t("user.role", { role: t(`role.${me.role}`) }) : t("header.noTenant")}
              </Menu.Label>
              <Menu.Separator className="my-1 h-px bg-[var(--color-border)]" />
              <Menu.Label className="px-2 py-1 text-xs text-[var(--color-muted)]">{t("user.theme")}</Menu.Label>
              <Menu.RadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
                {(["system", "light", "dark"] as const).map((v) => (
                  <Menu.RadioItem key={v} value={v} className={ITEM}>
                    <Menu.ItemIndicator>
                      <Check size={14} aria-hidden="true" />
                    </Menu.ItemIndicator>
                    {t(`user.theme.${v}`)}
                  </Menu.RadioItem>
                ))}
              </Menu.RadioGroup>
              <Menu.Separator className="my-1 h-px bg-[var(--color-border)]" />
              <Menu.Item className={ITEM} onSelect={() => void signOut()}>
                {t("user.signOut")}
              </Menu.Item>
            </Menu.Content>
          </Menu.Portal>
        </Menu.Root>
      </header>
      <div className="flex flex-1">
        <nav aria-label={t("nav.label")} className="w-56 shrink-0 border-r border-[var(--color-border)] p-2">
          <ul className="space-y-0.5">
            {SECTIONS.map((s) => (
              <li key={s.path}>
                <Link
                  to={s.path}
                  activeOptions={{ exact: s.path === "/" }}
                  className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm hover:bg-[var(--color-surface)] data-[status=active]:bg-[var(--color-surface)] data-[status=active]:font-semibold"
                >
                  <s.icon size={16} aria-hidden="true" />
                  <span className="flex-1">{t(s.label)}</span>
                  {s.release && <span className="text-xs text-[var(--color-muted)]">{s.release}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <main id="main" tabIndex={-1} className="flex-1 p-6 outline-none">
          {me.tenant_id ? (
            <Outlet />
          ) : (
            <Empty title={t("state.noTenant.title")}>
              <p>{t("state.noTenant.body", { email: me.user.email })}</p>
            </Empty>
          )}
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} onTheme={setTheme} onSignOut={signOut} />
    </div>
  );
}

function TenantSwitcher() {
  const t = useT();
  const me = useMe();
  const current = useCurrentTenant();
  const switchTenant = useSwitchTenant();
  const name = current?.name ?? t("header.noTenant");
  if (me.tenants.length < 2)
    return (
      <span className="flex items-center gap-2 text-sm text-[var(--color-muted)]">
        <Building2 size={16} aria-hidden="true" />
        {name}
      </span>
    );
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button variant="secondary" aria-label={t("header.tenant", { name })}>
          <Building2 size={16} aria-hidden="true" />
          {name}
          <ChevronDown size={14} aria-hidden="true" />
        </Button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content align="start" sideOffset={4} className={MENU}>
          <Menu.Label className="px-2 py-1.5 text-xs text-[var(--color-muted)]">{t("header.tenants")}</Menu.Label>
          <Menu.RadioGroup value={me.tenant_id ?? ""} onValueChange={(id) => void switchTenant(id)}>
            {me.tenants.map((tenant) => (
              <Menu.RadioItem key={tenant.id} value={tenant.id} className={ITEM}>
                <Menu.ItemIndicator>
                  <Check size={14} aria-hidden="true" />
                </Menu.ItemIndicator>
                <span className="flex-1">{tenant.name}</span>
                <span className="text-xs text-[var(--color-muted)]">{t(`role.${tenant.role}`)}</span>
              </Menu.RadioItem>
            ))}
          </Menu.RadioGroup>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
